import React from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/brand/Logo';
import { Mail, MapPin } from 'lucide-react';

// Premium minimal footer. Per brand direction it contains ONLY:
// - the firm logo
// - the firm name
// - the firm email
// - the firm registered address
// - the copyright notice
// Legally-required policy links (Privacy / Terms / Disclaimer) are kept as
// extremely subtle tertiary links beneath the copyright — they are required
// for compliance and would otherwise be unreachable from the public site.
export const Footer = () => {
    return (
        <footer data-testid="site-footer" className="relative bg-la-canvas border-t border-white/8">
            <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
                    {/* Brand block */}
                    <div className="md:col-span-6">
                        <Logo size={52} />
                        <p className="mt-7 font-display text-[22px] sm:text-[24px] leading-tight text-la-text">
                            Legal Alliance Consultancy
                        </p>
                        <p className="mt-3 text-[13.5px] leading-relaxed text-la-text3 max-w-sm">
                            A discreet London-based private advisory practice.
                        </p>
                    </div>

                    {/* Contact block */}
                    <div className="md:col-span-6 md:text-right">
                        <div className="meta-eyebrow text-la-text3">Contact</div>
                        <a
                            href="mailto:info@legal-ac.co.uk"
                            data-testid="footer-email"
                            className="mt-5 inline-flex items-center gap-2 text-[14px] text-la-text2 hover:text-la-text underline-grow"
                        >
                            <Mail className="h-3.5 w-3.5 text-la-gold" />
                            info@legal-ac.co.uk
                        </a>
                        <address className="mt-5 not-italic flex md:justify-end items-start gap-2 text-[13.5px] text-la-text2 leading-relaxed">
                            <MapPin className="h-3.5 w-3.5 text-la-gold mt-1 shrink-0" />
                            <span data-testid="footer-address">
                                167–169 Great Portland Street<br />
                                5th Floor<br />
                                London W1W 5PF
                            </span>
                        </address>
                    </div>
                </div>

                {/* Hairline gold accent divider */}
                <div
                    aria-hidden
                    className="mt-16 h-px"
                    style={{
                        background:
                            'linear-gradient(90deg, transparent 0%, rgba(201,168,106,0.30) 50%, transparent 100%)',
                    }}
                />

                {/* Copyright row */}
                <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <p data-testid="footer-copyright" className="text-[11.5px] text-la-text3 leading-relaxed">
                        © {new Date().getFullYear()} Legal Alliance Consultancy Ltd. All enquiries handled confidentially.
                    </p>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[10.5px] tracking-[0.16em] uppercase text-la-text3">
                        <Link to="/privacy" className="hover:text-la-text2 underline-grow">Privacy</Link>
                        <Link to="/terms" className="hover:text-la-text2 underline-grow">Terms</Link>
                        <Link to="/disclaimer" className="hover:text-la-text2 underline-grow">Disclaimer</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
